import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import lang from '../../lib/language';
import { ENROLL_REQUEST_STATE } from '../../lib/models/enroll';
import {
  ENROLL_AUTH_REQUEST_TYPE,
  REQUEST_CALL_NAME_CONFIRM_EMAIL,
  REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN,
} from '../../lib/models/enroll-requests';
import { RootState } from '../../lib/store';
import { enrollAuth } from '../../lib/store/auth';
import { login as serviceLogin } from '../../lib/store/common-actions';
import { parseOnboardingErrorCode } from '../../lib/store/crm/helpers';

import PortalLogoCarousel from '../../assets/img/ace_portal_logo_carousel.svg';
import { ClickableDiv, ClickableSpan } from '../../components';
import EmailConfirmedModal from '../../components/Enroll/EmailConfirmedModal/EmailConfirmedModal';
import ExpiredVerificationModal from '../../components/Enroll/ExpiredVerificationModal/ExpiredVerificationModal';
import LoginCarousel from '../../components/LoginCarousel/LoginCarousel';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { getRedeemSignupTokenParams, hasConfirmEmailToken } from '../../util/redeemSignupTokenUrl';
import { UI_ROUTES } from '../../util/routes';

import styles from './Login.module.scss';

type Props = ReduxProps;

const Login = (props: Props): JSX.Element => {
  const {
    login,
    appLoaded,
    responseStatus,
    responseStatusCode,
    requestCallName,
    tradingPlatformId,
    authenticate,
    redeemSignupToken,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();

  const [emailConfirmedModal, setEmailConfirmedModal] = useState<boolean>(false);
  const [isVerifyLinkExpired, setIsVerifyLinkExpired] = useState<boolean>(false);

  useEffect(() => {
    if ((hasConfirmEmailToken(location.search) || getRedeemSignupTokenParams(location.search)) && appLoaded) {
      const queryParams = new URLSearchParams(location.search);
      const redeemParams = getRedeemSignupTokenParams(location.search);

      if (redeemParams) {
        redeemSignupToken(
          redeemParams.username,
          redeemParams.signupToken,
          redeemParams.returnUrl,
          queryParams.get('FirstName'),
          queryParams.get('LastName'),
          queryParams.get('CountryOfResidence'),
          queryParams.get('clientId'),
        );
      } else {
        authenticate(
          queryParams.get('Email'),
          queryParams.get('token'),
          queryParams.get('FirstName'),
          queryParams.get('LastName'),
          queryParams.get('CountryOfResidence'),
          queryParams.get('clientId'),
        );
      }
    }
  }, [appLoaded, authenticate, redeemSignupToken, location.search]);

  useEffect(() => {
    if (tradingPlatformId) {
      setEmailConfirmedModal(true);
    }
  }, [tradingPlatformId]);

  useEffect(() => {
    if (
      responseStatus
      && responseStatus === ENROLL_REQUEST_STATE.ERROR
      && parseOnboardingErrorCode(responseStatusCode!) === ENROLL_REQUEST_STATE.REQUEST_ERROR
      && (
        requestCallName === REQUEST_CALL_NAME_CONFIRM_EMAIL
        || requestCallName === REQUEST_CALL_NAME_REDEEM_SIGNUP_TOKEN
      )
    ) {
      setIsVerifyLinkExpired(true);
    }
  }, [requestCallName, responseStatus, responseStatusCode]);

  const handleSignup = () => {
    navigate(UI_ROUTES.firstEnroll);
  };

  return (
    <>
      <div className={styles.wrapperContainer}>
        <div className={styles.containerLogin}>
          <div className={styles.logo}>
            <img src={PortalLogoCarousel} alt="Ace" />
          </div>
          <div className={styles.carousel}>
            <LoginCarousel />
          </div>
          <div className={styles.buttonsAndPolicies}>
            <>
              <ClickableDiv className={styles.clickableDiv} onClick={handleSignup}>
                <Button
                  className={styles.signUpBtn}
                  variant={ButtonTypesEnum.Primary}
                  id="signUpButton"
                  fullWidth
                >
                  {lang.commonOnboardingSignInButtonCreateAccount()}
                </Button>
              </ClickableDiv>
              <span className={styles.title}>
                {lang.mobileOnboardingAlreadyHaveAccount()}
                {' '}
                <ClickableSpan onClick={login} className={styles.logInButtonTitle}>
                  {lang.mobileOnboardingLogIn()}
                </ClickableSpan>
              </span>
            </>
          </div>
        </div>
        {emailConfirmedModal && <EmailConfirmedModal isOpened={emailConfirmedModal} />}
        {isVerifyLinkExpired && <ExpiredVerificationModal />}
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  login: () => {
    dispatch(serviceLogin({ authType: 'login' }));
  },
  authenticate: (
    email: string | null,
    token: string | null,
    firstName: string | null,
    lastName: string | null,
    country: string | null,
    referrerClientId: string | null,
  ) => {
    dispatch(
      enrollAuth({
        requestType: ENROLL_AUTH_REQUEST_TYPE.CONFIRM_EMAIL,
        email,
        token,
        firstName,
        lastName,
        country,
        referrerClientId,
      }),
    );
  },
  redeemSignupToken: (
    username: string,
    signupToken: string,
    returnUrl: string | undefined,
    firstName: string | null,
    lastName: string | null,
    country: string | null,
    referrerClientId: string | null,
  ) => {
    dispatch(enrollAuth({
      requestType: ENROLL_AUTH_REQUEST_TYPE.REDEEM_SIGNUP_TOKEN,
      username,
      signupToken,
      returnUrl,
      firstName,
      lastName,
      country,
      referrerClientId,
    }));
  },
});

const mapStateToProps = (state: RootState) => ({
  appLoaded: state.app.loaded,
  crmConnected: state.crm.connected,
  responseStatus: state.auth.enroll?.status,
  responseStatusCode: state.auth.enroll?.statusCode,
  requestCallName: state.auth.enroll?.requestCallName,
  tradingPlatformId: state.crm.tradingPlatformId,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Login);
